import { create } from 'zustand'
import { DATASET_MAP, DEFAULT_DATASET_ID } from './datasets'
import type { Dataset } from './datasets/types'
import type {
  ZarrLayer,
  QueryResult,
  TimeSeriesResult,
  TimeMeanResult,
  LoadingState,
} from '@carbonplan/zarr-layer'

export interface MapInstance {
  on(event: string, handler: (e: any) => void): unknown;
  off(event: string, handler: (e: any) => void): unknown;
  remove(): void;
  getLayer(id: string): unknown;
  removeLayer(id: string): void;
  addLayer(layer: any, beforeId?: string): unknown;
  setProjection(projection: any): unknown;
  resize(): void;
  getBounds(): { toArray(): [number, number][]; getWest(): number; getEast(): number; getSouth?(): number; getNorth?(): number; } | null;
  getZoom(): number;
  easeTo(options: { center: [number, number]; zoom: number }): void;
  getStyle(): { layers?: Array<{ id: string; type: string }> };
}

export type ExplorerScope = 'global' | 'collection'

interface AppState {
  sidebarWidth: number | null
  sidebarCollapsed: boolean
  rightRailWidth: number
  scope: ExplorerScope
  lockedDatasetId: string | null
  datasetId: string
  opacity: number
  clim: [number, number]
  colormap: string
  globeProjection: boolean
  datasetState: Record<string, unknown>
  loadingState: LoadingState
  pointResult: QueryResult | null
  regionResult: QueryResult | null
  timeSeriesResult: TimeSeriesResult | null
  timeSeriesLoading: boolean
  timeSeriesModeEnabled: boolean
  timeMeanEnabled: boolean
  /** UI flag: user has opened the mean panel (second lane visible + brush).
   *  Separate from timeMeanEnabled which reflects whether a mean is currently
   *  being *rendered* on the map. Mode-on can be true with no mean computed. */
  timeMeanMode: boolean
  timeMeanLoading: boolean
  timeMeanResult: TimeMeanResult | null
  timeMeanAutoClim: boolean
  timeMeanStartDate: string | null
  timeMeanEndDate: string | null
  reverseTimeIndex: ((date: string) => number) | null
  zarrLayerHidden: boolean
  timeSeriesWindow: number | 'all'
  timeSeriesAgg: 'none' | 'monthly' | 'yearly'
  timeSeriesUseMeanRange: boolean
  timeMeanWindow: number | 'all'
  timeStepsPerDay: number
  timeCount: number
  timeSliderHeight: number
  formatTimeIndex: ((i: number) => string) | null
  hoverQueryEnabled: boolean
  mapInstance: MapInstance | null
  zarrLayer: InstanceType<typeof ZarrLayer> | null
  setSidebarWidth: (width: number | null) => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setRightRailWidth: (w: number) => void
  setScope: (scope: ExplorerScope, lockedDatasetId?: string | null) => void
  setDatasetId: (id: string) => void
  setOpacity: (opacity: number) => void
  setClim: (clim: [number, number]) => void
  setColormap: (colormap: string) => void
  setGlobeProjection: (globeProjection: boolean) => void
  setActiveDatasetState: (updates: Record<string, unknown>) => void
  setLoadingState: (state: LoadingState) => void
  setPointResult: (result: QueryResult | null) => void
  setRegionResult: (result: QueryResult | null) => void
  setTimeSeriesResult: (result: TimeSeriesResult | null) => void
  setTimeSeriesLoading: (loading: boolean) => void
  setTimeSeriesModeEnabled: (enabled: boolean) => void
  setTimeMeanEnabled: (enabled: boolean) => void
  setTimeMeanMode: (v: boolean) => void
  setTimeMeanLoading: (loading: boolean) => void
  setTimeMeanResult: (result: TimeMeanResult | null) => void
  setTimeMeanAutoClim: (v: boolean) => void
  setTimeMeanStartDate: (d: string | null) => void
  setTimeMeanEndDate: (d: string | null) => void
  setReverseTimeIndex: (fn: ((date: string) => number) | null) => void
  setZarrLayerHidden: (hidden: boolean) => void
  setTimeSeriesWindow: (days: number | 'all') => void
  setTimeSeriesAgg: (agg: 'none' | 'monthly' | 'yearly') => void
  setTimeSeriesUseMeanRange: (v: boolean) => void
  setTimeMeanWindow: (days: number | 'all') => void
  setTimeStepsPerDay: (steps: number) => void
  setTimeCount: (count: number) => void
  setTimeSliderHeight: (h: number) => void
  setFormatTimeIndex: (fn: ((i: number) => string) | null) => void
  setHoverQueryEnabled: (enabled: boolean) => void
  setMapInstance: (map: MapInstance | null) => void
  setZarrLayer: (layer: InstanceType<typeof ZarrLayer> | null) => void
  getDatasetModule: () => Dataset
}

const defaultModule = DATASET_MAP[DEFAULT_DATASET_ID]

export const useAppStore = create<AppState>((set, get) => ({
  sidebarWidth: null,
  sidebarCollapsed: false,
  rightRailWidth: 0,
  scope: 'global',
  lockedDatasetId: null,
  datasetId: DEFAULT_DATASET_ID,
  opacity: 1,
  clim: defaultModule.clim,
  colormap: defaultModule.colormap,
  globeProjection: true,
  datasetState: { ...defaultModule.defaultState },
  loadingState: { loading: false, metadata: false, chunks: false },
  pointResult: null,
  regionResult: null,
  timeSeriesResult: null,
  timeSeriesLoading: false,
  timeSeriesModeEnabled: false,
  timeMeanEnabled: false,
  timeMeanMode: false,
  timeMeanLoading: false,
  timeMeanResult: null,
  timeMeanAutoClim: true,
  timeMeanStartDate: null,
  timeMeanEndDate: null,
  reverseTimeIndex: null,
  zarrLayerHidden: false,
  timeSeriesWindow: 30,
  timeSeriesAgg: 'none',
  timeSeriesUseMeanRange: false,
  timeMeanWindow: 30,
  timeStepsPerDay: 1,
  timeCount: 0,
  timeSliderHeight: 0,
  formatTimeIndex: null,
  hoverQueryEnabled: false,
  mapInstance: null,
  zarrLayer: null,
  setSidebarWidth: (width) => set({ sidebarWidth: width }),
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
  setRightRailWidth: (rightRailWidth) => set({ rightRailWidth }),
  setScope: (scope, lockedDatasetId = null) => set({ scope, lockedDatasetId }),
  setLoadingState: (loadingState) => set({ loadingState }),
  setDatasetId: (id) => {
    const module = DATASET_MAP[id]
    if (!module || id === get().datasetId) return

    set({
      datasetId: id,
      clim: module.clim,
      colormap: module.colormap,
      datasetState: { ...module.defaultState },
      timeMeanStartDate: null,
      timeMeanEndDate: null,
    })

    if (module.center && module.zoom != null) {
      get().mapInstance?.easeTo({ center: module.center, zoom: module.zoom })
    }
  },
  setOpacity: (opacity) => set({ opacity }),
  setClim: (clim) => set({ clim }),
  setColormap: (colormap) => set({ colormap }),
  setGlobeProjection: (globeProjection) => set({ globeProjection }),
  setActiveDatasetState: (updates) => {
    set((state) => ({
      datasetState: { ...state.datasetState, ...updates },
    }))
  },
  setPointResult: (pointResult) => set({ pointResult }),
  setRegionResult: (regionResult) => set({ regionResult }),
  setTimeSeriesResult: (timeSeriesResult) => set({ timeSeriesResult }),
  setTimeSeriesLoading: (timeSeriesLoading) => set({ timeSeriesLoading }),
  setTimeSeriesModeEnabled: (timeSeriesModeEnabled) =>
    set({ timeSeriesModeEnabled }),
  setTimeMeanEnabled: (timeMeanEnabled) => set({ timeMeanEnabled }),
  setTimeMeanMode: (timeMeanMode) => set({ timeMeanMode }),
  setTimeMeanLoading: (timeMeanLoading) => set({ timeMeanLoading }),
  setTimeMeanResult: (timeMeanResult) => set({ timeMeanResult }),
  setTimeMeanAutoClim: (timeMeanAutoClim) => set({ timeMeanAutoClim }),
  setTimeMeanStartDate: (timeMeanStartDate) => set({ timeMeanStartDate }),
  setTimeMeanEndDate: (timeMeanEndDate) => set({ timeMeanEndDate }),
  setReverseTimeIndex: (reverseTimeIndex) => set({ reverseTimeIndex }),
  setZarrLayerHidden: (zarrLayerHidden) => set({ zarrLayerHidden }),
  setTimeSeriesWindow: (timeSeriesWindow) => set({ timeSeriesWindow }),
  setTimeSeriesAgg: (timeSeriesAgg) => set({ timeSeriesAgg }),
  setTimeSeriesUseMeanRange: (timeSeriesUseMeanRange) =>
    set({ timeSeriesUseMeanRange }),
  setTimeMeanWindow: (timeMeanWindow) => set({ timeMeanWindow }),
  setTimeStepsPerDay: (timeStepsPerDay) => set({ timeStepsPerDay }),
  setTimeCount: (timeCount) => set({ timeCount }),
  setTimeSliderHeight: (timeSliderHeight) => set({ timeSliderHeight }),
  setFormatTimeIndex: (formatTimeIndex) => set({ formatTimeIndex }),
  setHoverQueryEnabled: (hoverQueryEnabled) => set({ hoverQueryEnabled }),
  setMapInstance: (mapInstance) => set({ mapInstance }),
  setZarrLayer: (zarrLayer) => set({ zarrLayer }),
  getDatasetModule: () => DATASET_MAP[get().datasetId],
}))
